import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Database, 
  Eye, 
  EyeOff,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  Settings,
  AlertCircle,
  Check,
  RefreshCw,
  Info,
  Sparkles,
  Lock,
  Unlock,
  ArrowUp,
  ArrowDown,
  List as ListIcon,
  GripVertical,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Table,
  Columns
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { clearConfigCache } from '../../../utils/entityConfig';

const TenantEntityConfig = () => {
  const { user } = useAuth();
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityConfig, setEntityConfig] = useState(null);
  const [overrides, setOverrides] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedEntity, setExpandedEntity] = useState(null);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationResults, setValidationResults] = useState(null);

  const tenantCode =
    user?.selectedTenant?.code ||
    user?.selectedTenant?.tenant_code ||
    user?.tenantCode ||
    null;

  const notifyConfigUpdated = useCallback((entityName) => {
    if (!entityName) return;
    const normalized = entityName.toLowerCase();
    clearConfigCache(normalized);
    window.dispatchEvent(
      new CustomEvent('entity-config-updated', {
        detail: { entityName: normalized }
      })
    );
  }, []);

  useEffect(() => {
    fetchEntities();
    // Reset selected entity when tenant changes
    setSelectedEntity(null);
    setEntityConfig(null);
    setOverrides(null);
    setExpandedEntity(null);
  }, [tenantCode]);

  const fetchEntities = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || '';
      const headers = {
        'Authorization': `Bearer ${token}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      };
      const response = await fetch('/api/tenant-entity-config/entities/list', {
        headers
      });

      if (!response.ok) throw new Error('Failed to fetch entities');

      const data = await response.json();
      // Ensure entities is always an array
      const entitiesData = data.entities;
      if (Array.isArray(entitiesData)) {
        setEntities(entitiesData);
      } else {
        console.warn('⚠️ Expected entities to be an array, got:', typeof entitiesData, entitiesData);
        setEntities([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntityConfig = async (entityName) => {
    try {
      setLoading(true);
      
      // Fetch merged config
      const token = localStorage.getItem('token') || '';
      const headers = {
        'Authorization': `Bearer ${token}`,
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      };
      const configResponse = await fetch(`/api/tenant-entity-config/${entityName}`, {
        headers
      });
      if (!configResponse.ok) throw new Error('Failed to fetch entity config');
      const configData = await configResponse.json();
      
      // Fetch overrides
      const overridesResponse = await fetch(`/api/tenant-entity-config/${entityName}/overrides`, {
        headers
      });
      if (!overridesResponse.ok) throw new Error('Failed to fetch overrides');
      const overridesData = await overridesResponse.json();
      
      // Validate config structure
      const config = configData.config || {};
      if (!config.entity) {
        console.warn(`⚠️ Entity config for ${entityName} missing 'entity' property`);
        config.entity = { name: entityName, displayName: entityName };
      }
      if (!config.fields) {
        console.warn(`⚠️ Entity config for ${entityName} missing 'fields' property`);
        config.fields = {};
      }
      if (!config.views) {
        console.warn(`⚠️ Entity config for ${entityName} missing 'views' property`);
        config.views = { list: {}, detail: {}, form: {} };
      }
      
      setEntityConfig(config);
      setOverrides(overridesData.overrides || {});
      setSelectedEntity(entityName);
      setExpandedEntity(entityName);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFieldVisibility = async (fieldName, currentlyHidden) => {
    try {
      setSaving(true);
      
      const endpoint = currentlyHidden ? 'show-field' : 'hide-field';
      const token = localStorage.getItem('token') || '';
      const response = await fetch(`/api/tenant-entity-config/${selectedEntity}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        },
        body: JSON.stringify({ fieldName })
      });

      if (!response.ok) throw new Error('Failed to toggle field visibility');

      // Refresh config
      await fetchEntityConfig(selectedEntity);
      notifyConfigUpdated(selectedEntity);
      
      alert(`Field "${fieldName}" ${currentlyHidden ? 'shown' : 'hidden'} successfully!`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomField = async (fieldName) => {
    if (!window.confirm(`Delete custom field "${fieldName}"? This cannot be undone.`)) {
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token') || '';
      
      const response = await fetch(
        `/api/tenant-entity-config/${selectedEntity}/custom-field/${fieldName}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
          }
        }
      );

      if (!response.ok) throw new Error('Failed to delete custom field');

      // Refresh config
      await fetchEntityConfig(selectedEntity);
      notifyConfigUpdated(selectedEntity);
      
      alert(`Custom field "${fieldName}" deleted successfully!`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleValidateEntity = async (entityName) => {
    try {
      const response = await fetch(`/api/tenant-entity-config/validate/${entityName}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to validate entity configuration');
      }

      const data = await response.json();
      setValidationResults(data.validation);
      setSelectedEntity(entityName);
      setShowValidationModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleValidateAll = async () => {
    try {
      const response = await fetch('/api/tenant-entity-config/validate-all', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to validate entity configurations');
      }

      const data = await response.json();
      const invalidEntities = data.validations || [];

      if (invalidEntities.length === 0) {
        alert('✅ All entities validated successfully!\n\nNo issues found.');
      } else {
        const summary = invalidEntities.map(v => 
          `• ${v.entityName}: ${v.invalidFields.length} invalid field(s)`
        ).join('\n');
        
        alert(
          `⚠️ Validation Issues Found\n\n` +
          `${invalidEntities.length} entity(es) have validation issues:\n\n${summary}\n\n` +
          `Opening first entity with issues...`
        );
        
        // Show first entity with issues
        setValidationResults(invalidEntities[0]);
        setSelectedEntity(invalidEntities[0].entityName);
        setShowValidationModal(true);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFieldsRemoved = () => {
    setShowValidationModal(false);
    setValidationResults(null);
    fetchEntities();
    if (selectedEntity) {
      fetchEntityConfig(selectedEntity);
    }
  };

  if (loading && !entityConfig) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <Settings className="h-7 w-7" />
            <span>Entity Configuration</span>
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Customize entity fields for your organization
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleValidateAll}
            className="bg-orange-600 dark:bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-700 dark:hover:bg-orange-600 flex items-center space-x-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            <span>Validate All</span>
          </button>
          <button
            onClick={fetchEntities}
            className="bg-gray-600 dark:bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <p className="font-semibold mb-1">Customize Your Experience</p>
            <p>Configure list views, customize field display names (click the <Edit className="h-3 w-3 inline" /> icon), hide fields you don't need, and add custom fields specific to your organization.</p>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left: Entity List */}
        <div className="col-span-4">
          <EntityListPanel
            entities={entities}
            selectedEntity={selectedEntity}
            onSelectEntity={fetchEntityConfig}
            onValidate={handleValidateEntity}
          />
        </div>

        {/* Right: Entity Configuration */}
        <div className="col-span-8">
          {entityConfig ? (
            <EntityConfigPanel
              entityName={selectedEntity}
              config={entityConfig}
              overrides={overrides}
              tenantCode={tenantCode}
              onToggleField={handleToggleFieldVisibility}
              onDeleteCustomField={handleDeleteCustomField}
              onAddField={() => setShowAddFieldModal(true)}
              onEditField={(fieldName, fieldConfig, isCustom) => {
                const overrideConfig =
                  overrides?.customFields?.[fieldName] || 
                  overrides?.modifiedFields?.[fieldName] || 
                  null;
                setEditingField({
                  name: fieldName,
                  fieldConfig,
                  overrideConfig,
                  isCustom
                });
              }}
              saving={saving}
              onRefresh={() => fetchEntityConfig(selectedEntity)}
              onConfigUpdated={notifyConfigUpdated}
            />
          ) : (
            <EmptyStatePanel />
          )}
        </div>
      </div>

      {/* Add Field Modal */}
      {showAddFieldModal && (
        <AddCustomFieldModal
          entityName={selectedEntity}
          tenantCode={tenantCode}
          onClose={() => setShowAddFieldModal(false)}
          onSuccess={() => {
            setShowAddFieldModal(false);
            fetchEntityConfig(selectedEntity);
          }}
          onConfigUpdated={notifyConfigUpdated}
        />
      )}

      {/* Edit Field Modal */}
      {editingField && (
        <EditFieldModal
          entityName={selectedEntity}
          fieldName={editingField.name}
          fieldConfig={editingField.fieldConfig}
          overrideConfig={editingField.overrideConfig}
          tenantCode={tenantCode}
          isCustom={editingField.isCustom}
          onClose={() => setEditingField(null)}
          onSuccess={() => {
            setEditingField(null);
            fetchEntityConfig(selectedEntity);
          }}
          onConfigUpdated={notifyConfigUpdated}
        />
      )}

      {/* Validation Modal */}
      {showValidationModal && validationResults && (
        <ValidationModal
          entityName={selectedEntity}
          validation={validationResults}
          onClose={() => {
            setShowValidationModal(false);
            setValidationResults(null);
          }}
          onFieldsRemoved={handleFieldsRemoved}
        />
      )}
    </div>
  );
};

// Entity List Panel
const EntityListPanel = ({ entities, selectedEntity, onSelectEntity, onValidate }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Entities</h2>
      </div>

      <div className="max-h-[calc(100vh-350px)] overflow-y-auto">
        {Array.isArray(entities) && entities.length > 0 ? (
          entities.map((entity) => (
            <div
              key={entity?.entityName || 'unknown'}
              className={`group relative border-b border-gray-200 dark:border-gray-700 transition-colors ${
                selectedEntity === entity?.entityName
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-600'
                  : ''
              }`}
            >
              <button
                onClick={() => onSelectEntity(entity?.entityName)}
                className="w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-start space-x-3">
                  <Database className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {entity?.displayName || entity?.entityName || 'Unknown Entity'}
                    </div>
                    {entity?.hasOverrides && (
                      <div className="flex items-center space-x-2 mt-1">
                        {(entity?.hiddenFieldCount || 0) > 0 && (
                          <span className="text-xs px-2 py-0.5 bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded">
                            {entity.hiddenFieldCount} hidden
                          </span>
                        )}
                        {(entity?.customFieldCount || 0) > 0 && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded">
                            {entity.customFieldCount} custom
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (entity?.entityName) {
                    onValidate(entity.entityName);
                  }
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-2 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded"
                title="Validate Against Schema"
              >
                <CheckCircle2 className="h-4 w-4" />
              </button>
            </div>
          ))
        ) : (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Database className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No entities found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Entity Configuration Panel
const EntityConfigPanel = ({
  entityName,
  config,
  overrides,
  tenantCode,
  onToggleField,
  onDeleteCustomField,
  onAddField,
  onEditField,
  saving,
  onRefresh,
  onConfigUpdated
}) => {
  const [filterType, setFilterType] = useState('all'); // 'all', 'visible', 'hidden', 'custom'

  const fields = Object.entries(config.fields || {});
  const hiddenFields = overrides.hiddenFields || [];
  const customFields = Object.keys(overrides.customFields || {});
  
  // Count fields with autofill (suggestFromData) enabled
  const autofillFields = fields.filter(([_, fieldConfig]) => 
    fieldConfig?.ui?.suggestFromData?.enabled === true
  ).map(([fieldName]) => fieldName);

  const filteredFields = fields.filter(([fieldName, fieldConfig]) => {
    if (filterType === 'all') return true;
    if (filterType === 'visible') return !hiddenFields.includes(fieldName);
    if (filterType === 'hidden') return hiddenFields.includes(fieldName);
    if (filterType === 'custom') return customFields.includes(fieldName);
    if (filterType === 'autofill') return fieldConfig?.ui?.suggestFromData?.enabled === true;
    return true;
  });

  const handleUpdateDisplayNames = async (fieldName, displayNameEN, displayNameTH) => {
    try {
      const token = localStorage.getItem('token') || '';
      const response = await fetch(
        `/api/tenant-entity-config/${entityName}/field-display-name`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
          },
          body: JSON.stringify({
            fieldName,
            displayName: displayNameEN,
            displayNameTH: displayNameTH || displayNameEN
          })
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to update display names');
      }

      // Refresh config
      await onRefresh();
      if (onConfigUpdated) {
        onConfigUpdated(entityName);
      }
    } catch (error) {
      console.error('Error updating display names:', error);
      throw error;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
              {config?.entity?.displayName || config?.entity?.name || entityName || 'Entity'} Configuration
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {fields.length} total fields • {hiddenFields.length} hidden • {customFields.length} custom • {autofillFields.length} autofill
            </p>
          </div>
          <button
            onClick={onAddField}
            className="bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Field</span>
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'All', count: fields.length },
            { value: 'visible', label: 'Visible', count: fields.length - hiddenFields.length },
            { value: 'hidden', label: 'Hidden', count: hiddenFields.length },
            { value: 'custom', label: 'Custom', count: customFields.length },
            { value: 'autofill', label: 'Autofill', count: autofillFields.length, color: 'cyan' }
          ].map(tab => (
            <button
              key={tab.value}
              onClick={() => setFilterType(tab.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterType === tab.value
                  ? tab.color === 'cyan'
                    ? 'bg-cyan-100 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300'
                    : 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* List View Configuration Section */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 py-4">
          <ListViewConfigurator
            entityName={entityName}
            config={config}
            overrides={overrides}
            tenantCode={tenantCode}
            onRefresh={onRefresh}
            onConfigUpdated={onConfigUpdated}
          />
        </div>
      </div>

      {/* All Fields Section */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          All Fields
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Manage field visibility, edit custom fields, and configure display names
        </p>
      </div>

      {/* Fields List */}
      <div className="max-h-[calc(100vh-550px)] overflow-y-auto">
        {filteredFields.map(([fieldName, fieldConfig]) => {
          const isHidden = hiddenFields.includes(fieldName);
          const isCustom = customFields.includes(fieldName);
          const isPrimaryKey = fieldName === config?.entity?.primaryKey;

          return (
            <FieldRow
              key={fieldName}
              fieldName={fieldName}
              fieldConfig={fieldConfig}
              isHidden={isHidden}
              isCustom={isCustom}
              isPrimaryKey={isPrimaryKey}
              onToggleVisibility={() => onToggleField(fieldName, isHidden)}
              onDelete={() => onDeleteCustomField(fieldName)}
              onEdit={() => onEditField(fieldName, fieldConfig, isCustom)}
              onUpdateDisplayNames={handleUpdateDisplayNames}
              saving={saving}
            />
          );
        })}

        {filteredFields.length === 0 && (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Settings className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No fields match the current filter</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ListViewConfigurator = ({
  entityName,
  config,
  overrides,
  tenantCode,
  onRefresh,
  onConfigUpdated
}) => {
  const [visibleFields, setVisibleFields] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(null); // null = main list view

  // Get available tabs from config
  const tabs = useMemo(() => {
    if (!config?.views?.list?.tabs) {
      console.log('No tabs found in config.views.list.tabs');
      return [];
    }
    const processedTabs = config.views?.list?.tabs
      .map(tab => ({
        ...tab,
        name: tab.name || tab.id, // Support both name and id
        displayName: tab.displayName || tab.label || tab.name || tab.id
      }))
      .sort((a, b) => (a.order || 999) - (b.order || 999));
    console.log('Processed tabs:', processedTabs);
    return processedTabs;
  }, [config]);

  const sortAvailable = useCallback((fields) => {
    return [...fields].sort((a, b) => {
      if (a.recommended === b.recommended) {
        return (a.displayName || a.name).localeCompare(b.displayName || b.name);
      }
      return a.recommended ? -1 : 1;
    });
  }, []);

  const buildStateFromConfig = useCallback(
    (layoutOverride, tabName = null) => {
      if (!config || !config.fields) {
        return {
          visible: [],
          available: [],
          defaultVisibleNames: []
        };
      }

      const fieldMap = config.fields;
      const allEntries = Object.entries(fieldMap);

      // Get default visible fields based on tab or main list view
      let defaultVisibleNames = [];
      if (tabName && config.views?.list?.tabs) {
        const tab = config.views?.list?.tabs?.find(t => (t.name || t.id) === tabName);
        if (tab && tab.fields) {
          defaultVisibleNames = tab.fields.filter(
            fieldName => fieldMap[fieldName] && !fieldMap[fieldName]._tenantHidden
          );
        }
      } else {
        defaultVisibleNames = allEntries
          .filter(
            ([, field]) => !field._tenantHidden && field.ui?.showInList
          )
          .sort(
            (a, b) =>
              (a[1].ui?.order ?? 999) - (b[1].ui?.order ?? 999)
          )
          .map(([name]) => name);
      }

      let baseLayout =
        Array.isArray(layoutOverride) && layoutOverride.length > 0
          ? layoutOverride.filter(
              (fieldName) =>
                fieldMap[fieldName] && !fieldMap[fieldName]._tenantHidden
            )
          : defaultVisibleNames;

      const seen = new Set();
      const visible = [];

      baseLayout.forEach((fieldName) => {
        if (
          !fieldMap[fieldName] ||
          fieldMap[fieldName]._tenantHidden ||
          seen.has(fieldName)
        ) {
          return;
        }
        seen.add(fieldName);
        const field = fieldMap[fieldName];
        visible.push({
          name: fieldName,
          displayName: field.displayName || fieldName,
          dataType: field.dataType || '',
          isCustom: Boolean(field._tenantSpecific),
          recommended: field.ui?.showInList ?? false
        });
      });

      const available = [];
      allEntries.forEach(([fieldName, field]) => {
        if (seen.has(fieldName) || field._tenantHidden) return;
        available.push({
          name: fieldName,
          displayName: field.displayName || fieldName,
          dataType: field.dataType || '',
          isCustom: Boolean(field._tenantSpecific),
          recommended: field.ui?.showInList ?? false
        });
      });

      return {
        visible,
        available: sortAvailable(available),
        defaultVisibleNames
      };
    },
    [config, sortAvailable]
  );

  useEffect(() => {
    setMessage(null);
    setError(null);
    setActiveTab(null); // Reset to main view when entity changes
  }, [entityName]);

  useEffect(() => {
    // Get the appropriate layout override based on active tab
    let layoutOverride = null;
    if (activeTab) {
      layoutOverride = overrides?.listView?.tabs?.[activeTab]?.layout || null;
    } else {
      layoutOverride = overrides?.listView?.layout || null;
    }
    
    const { visible, available } = buildStateFromConfig(layoutOverride, activeTab);
    setVisibleFields(visible);
    setAvailableFields(available);
  }, [buildStateFromConfig, overrides, activeTab]);

  const originalLayout = useMemo(() => {
    let layoutOverride = null;
    if (activeTab) {
      layoutOverride = overrides?.listView?.tabs?.[activeTab]?.layout || null;
    } else {
      layoutOverride = overrides?.listView?.layout || null;
    }
    return buildStateFromConfig(layoutOverride, activeTab).visible.map((field) => field.name);
  }, [buildStateFromConfig, overrides, activeTab]);

  const currentLayout = useMemo(
    () => visibleFields.map((field) => field.name),
    [visibleFields]
  );

  const layoutsEqual = useCallback((a, b) => {
    if (a.length !== b.length) return false;
    return a.every((value, index) => value === b[index]);
  }, []);

  const hasChanges = !layoutsEqual(currentLayout, originalLayout);
  const hasOverrides = activeTab
    ? Array.isArray(overrides?.listView?.tabs?.[activeTab]?.layout) &&
      overrides.listView.tabs[activeTab].layout.length > 0
    : Array.isArray(overrides?.listView?.layout) &&
      overrides.listView.layout.length > 0;

  const handleMove = (index, offset) => {
    setVisibleFields((prev) => {
      const targetIndex = index + offset;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(targetIndex, 0, item);
      return next;
    });
    setMessage(null);
  };

  const handleRemoveField = (fieldName) => {
    const field = visibleFields.find((f) => f.name === fieldName);
    if (!field) return;
    setVisibleFields((prev) => prev.filter((f) => f.name !== fieldName));
    setAvailableFields((prev) =>
      sortAvailable([...prev, field])
    );
    setMessage(null);
  };

  const handleAddField = (fieldName) => {
    const field = availableFields.find((f) => f.name === fieldName);
    if (!field) return;
    setVisibleFields((prev) => [...prev, field]);
    setAvailableFields((prev) =>
      sortAvailable(prev.filter((f) => f.name !== fieldName))
    );
    setMessage(null);
  };

  const saveLayout = async (layoutToSave) => {
    try {
      setSaving(true);
      setError(null);
      setMessage(null);

      const token = localStorage.getItem('token') || '';
      
      // Prepare the body based on whether we're saving a tab or main view
      const body = activeTab
        ? { layout: layoutToSave, tabName: activeTab }
        : { layout: layoutToSave };
      
      const response = await fetch(
        `/api/tenant-entity-config/${entityName}/list-view`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
          },
          body: JSON.stringify(body)
        }
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload.message || 'Failed to save list view configuration'
        );
      }

      setMessage(
        payload.message || 'List view configuration updated successfully'
      );

      if (onRefresh) {
        await onRefresh();
      }
      if (onConfigUpdated) {
        onConfigUpdated(entityName);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    saveLayout(currentLayout);
  };

  const handleReset = () => {
    const { visible, available } = buildStateFromConfig(null, activeTab);
    setVisibleFields(visible);
    setAvailableFields(available);
    saveLayout([]);
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <ListIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Landing List View Configuration
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {tabs.length > 0 
                ? 'Configure fields for each tab or the main list view'
                : 'Choose which fields appear on the first landing list and arrange their order'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleReset}
          disabled={saving || (!hasOverrides && !hasChanges)}
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reset to Global Default
        </button>
      </div>

      {/* Tab Selector */}
      {tabs.length > 0 && (
        <div className="mb-4">
          <div className="flex space-x-2 overflow-x-auto pb-2">
            <button
              onClick={() => setActiveTab(null)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === null
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              Main List View
            </button>
            {tabs.map(tab => (
              <button
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.name
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {tab.displayName || tab.name}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {activeTab 
              ? `Configuring fields for the "${tabs.find(t => t.name === activeTab)?.displayName || activeTab}" tab`
              : 'Configuring the default list view (used when no specific tab is active)'}
          </p>
        </div>
      )}

      {error && (
        <div className="mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-3 py-2 text-xs text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {message && (
        <div className="mb-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg px-3 py-2 text-xs text-green-700 dark:text-green-300">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
            Visible Columns ({visibleFields.length})
          </h4>

          {visibleFields.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
              No fields selected. Add fields from the list on the right.
            </div>
          ) : (
            <div className="space-y-2">
              {visibleFields.map((field, index) => (
                <div
                  key={field.name}
                  className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
                >
                  <div className="flex items-center space-x-3">
                    <GripVertical className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {field.displayName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {field.name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      type="button"
                      onClick={() => handleMove(index, -1)}
                      disabled={index === 0 || saving}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ArrowUp className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMove(index, 1)}
                      disabled={index === visibleFields.length - 1 || saving}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ArrowDown className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveField(field.name)}
                      disabled={saving}
                      className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                      title="Remove from list view"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
            Available Fields ({availableFields.length})
          </h4>

          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {availableFields.map((field) => (
              <div
                key={field.name}
                className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
              >
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {field.displayName}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-2">
                    <span>{field.name}</span>
                    {field.recommended && (
                      <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full text-[10px] uppercase tracking-wide">
                        Recommended
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleAddField(field.name)}
                  disabled={saving}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                >
                  Add
                </button>
              </div>
            ))}

            {availableFields.length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                All fields are currently visible.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save List View
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Field Row Component with inline display name editing
const FieldRow = ({
  fieldName,
  fieldConfig,
  isHidden,
  isCustom,
  isPrimaryKey,
  onToggleVisibility,
  onDelete,
  onEdit,
  onUpdateDisplayNames,
  saving
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [displayNameEN, setDisplayNameEN] = useState(fieldConfig.displayName || fieldName);
  const [displayNameTH, setDisplayNameTH] = useState(fieldConfig.displayNameTH || fieldConfig.displayName || fieldName);
  const [savingDisplayName, setSavingDisplayName] = useState(false);

  const hasDisplayNameOverride = fieldConfig._tenantModified;

  const handleSaveDisplayNames = async () => {
    if (!displayNameEN.trim()) {
      alert('Display Name (EN) is required');
      return;
    }

    setSavingDisplayName(true);
    try {
      await onUpdateDisplayNames(fieldName, displayNameEN.trim(), displayNameTH.trim());
      setEditingDisplayName(false);
    } catch (error) {
      console.error('Failed to update display names:', error);
      alert('Failed to update display names');
    } finally {
      setSavingDisplayName(false);
    }
  };

  const handleCancelEdit = () => {
    setDisplayNameEN(fieldConfig.displayName || fieldName);
    setDisplayNameTH(fieldConfig.displayNameTH || fieldConfig.displayName || fieldName);
    setEditingDisplayName(false);
  };

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
      <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showDetails ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
              
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {fieldName}
                  </span>
                  
                  {/* Badges */}
                  <div className="flex items-center space-x-1">
                    {isPrimaryKey && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded flex items-center space-x-1">
                        <Lock className="h-3 w-3" />
                        <span>Primary Key</span>
                      </span>
                    )}
                    {isCustom && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded flex items-center space-x-1">
                        <Sparkles className="h-3 w-3" />
                        <span>Custom</span>
                      </span>
                    )}
                    {isHidden && (
                      <span className="px-2 py-0.5 text-xs bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded flex items-center space-x-1">
                        <EyeOff className="h-3 w-3" />
                        <span>Hidden</span>
                      </span>
                    )}
                    {hasDisplayNameOverride && !isCustom && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded flex items-center space-x-1">
                        <Edit className="h-3 w-3" />
                        <span>Customized</span>
                      </span>
                    )}
                    {fieldConfig?.ui?.suggestFromData?.enabled && (
                      <span className="px-2 py-0.5 text-xs bg-cyan-100 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300 rounded flex items-center space-x-1" title="Autocomplete from existing data">
                        <ListIcon className="h-3 w-3" />
                        <span>Autofill</span>
                      </span>
                    )}
                  </div>
                </div>
                
                {!editingDisplayName ? (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <div className="flex items-center space-x-2">
                      <span>EN: {fieldConfig.displayName}</span>
                      {fieldConfig.displayNameTH && (
                        <>
                          <span>•</span>
                          <span>TH: {fieldConfig.displayNameTH}</span>
                        </>
                      )}
                      {!isPrimaryKey && (
                        <button
                          onClick={() => setEditingDisplayName(true)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-xs"
                          title="Edit display names"
                        >
                          <Edit className="h-3 w-3 inline" />
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Display Name (EN) <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="text"
                          value={displayNameEN}
                          onChange={(e) => setDisplayNameEN(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Display Name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Display Name (TH)
                        </label>
                        <input
                          type="text"
                          value={displayNameTH}
                          onChange={(e) => setDisplayNameTH(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="ชื่อแสดง"
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleSaveDisplayNames}
                        disabled={savingDisplayName}
                        className="px-3 py-1 text-xs bg-blue-600 dark:bg-blue-500 text-white rounded hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50"
                      >
                        {savingDisplayName ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={savingDisplayName}
                        className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Expanded Details */}
            {showDetails && (
              <div className="mt-3 ml-6 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Display Name (EN):</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">
                    {fieldConfig.displayName || fieldName}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Display Name (TH):</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">
                    {fieldConfig.displayNameTH || fieldConfig.displayName || fieldName}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Description:</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">
                    {fieldConfig.description || 'No description'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Data Type:</span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">
                    {fieldConfig.dataType}
                  </span>
                </div>
                {fieldConfig.validation?.required && (
                  <div className="text-red-600 dark:text-red-400">
                    ✓ Required field
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 ml-4">
            {!isPrimaryKey && (
              <>
                <button
                  onClick={onToggleVisibility}
                  disabled={saving}
                  className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${
                    isHidden
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-green-600 dark:text-green-400'
                  }`}
                  title={isHidden ? 'Show field' : 'Hide field'}
                >
                  {isHidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
                
                {/* Edit button for all fields (custom and global) */}
                <button
                  onClick={onEdit}
                  disabled={saving}
                  className="p-2 text-blue-600 dark:text-blue-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  title={isCustom ? "Edit custom field" : "Configure field settings"}
                >
                  <Settings className="h-4 w-4" />
                </button>
                
                {isCustom && (
                  <button
                    onClick={onDelete}
                    disabled={saving}
                    className="p-2 text-red-600 dark:text-red-400 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                    title="Delete custom field"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Empty State Panel
const EmptyStatePanel = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 h-full flex items-center justify-center">
      <div className="text-center p-12">
        <Database className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          No Entity Selected
        </h3>
        <p className="text-gray-600 dark:text-gray-300">
          Select an entity from the list to configure its fields
        </p>
      </div>
    </div>
  );
};

// Add Custom Field Modal
const AddCustomFieldModal = ({
  entityName,
  tenantCode,
  onClose,
  onSuccess,
  onConfigUpdated
}) => {
  const [fieldData, setFieldData] = useState({
    fieldName: '',
    displayName: '',
    displayNameTH: '',
    description: '',
    dataType: 'string',
    required: false,
    maxLength: '',
    inputType: 'text',
    showInList: true,
    showInForm: true,
    showInDetail: true,
    order: 100,
    suggestFromData: false,
    suggestMinChars: 0,
    suggestMaxSuggestions: 20
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingMapping, setGeneratingMapping] = useState(false);
  const [aiMapping, setAiMapping] = useState(null);

  const generateDatabaseMapping = async () => {
    setGeneratingMapping(true);
    setError(null);

    try {
      const token = localStorage.getItem('token') || '';
      const response = await fetch(
        `/api/tenant-entity-config/${entityName}/generate-mapping`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
          },
          body: JSON.stringify({
            fieldName: fieldData.fieldName,
            displayName: fieldData.displayName,
            description: fieldData.description,
            dataType: fieldData.dataType,
            required: fieldData.required
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to generate mapping');
      }

      const data = await response.json();
      setAiMapping(data.mapping);
      
      return data.mapping;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setGeneratingMapping(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Generate database mapping using AI if not already generated
      let databaseMapping = aiMapping;
      if (!databaseMapping) {
        databaseMapping = await generateDatabaseMapping();
      }

      const fieldConfig = {
        displayName: fieldData.displayName,
        description: fieldData.description,
        dataType: fieldData.dataType,
        database: databaseMapping,
        displayNameTH: fieldData.displayNameTH || fieldData.displayName,
        validation: {
          required: fieldData.required,
          ...(fieldData.maxLength && { maxLength: parseInt(fieldData.maxLength) })
        },
        ui: {
          inputType: fieldData.inputType,
          showInList: fieldData.showInList,
          showInForm: fieldData.showInForm,
          showInDetail: fieldData.showInDetail,
          showInExport: true,
          order: parseInt(fieldData.order),
          ...(fieldData.suggestFromData && fieldData.inputType === 'text' && {
            suggestFromData: {
              enabled: true,
              minChars: parseInt(fieldData.suggestMinChars) || 0,
              maxSuggestions: parseInt(fieldData.suggestMaxSuggestions) || 20
            }
          })
        }
      };

      const token = localStorage.getItem('token') || '';
      const response = await fetch(
        `/api/tenant-entity-config/${entityName}/custom-field`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
          },
          body: JSON.stringify({
            fieldName: fieldData.fieldName,
            fieldConfig
          })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to add custom field');
      }

      alert('Custom field added successfully!');
      if (onConfigUpdated) {
        onConfigUpdated(entityName);
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Add Custom Field
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* AI Info Banner */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-3 flex items-start space-x-2">
            <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-purple-700 dark:text-purple-300">
              <p className="font-semibold">AI-Powered Database Mapping</p>
              <p className="mt-1">Database schema (table, column, SQL type) will be automatically generated by AI based on your field description.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Field Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={fieldData.fieldName}
                onChange={(e) => setFieldData({ ...fieldData, fieldName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., LineID"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Display Name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={fieldData.displayName}
                onChange={(e) => setFieldData({ ...fieldData, displayName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., LINE ID"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Display Name (Thai)
              </label>
              <input
                type="text"
                value={fieldData.displayNameTH}
                onChange={(e) => setFieldData({ ...fieldData, displayNameTH: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="เช่น ไอดีไลน์"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={fieldData.description}
              onChange={(e) => setFieldData({ ...fieldData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows="2"
              placeholder="Brief description of this field"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Data Type <span className="text-red-600">*</span>
              </label>
              <select
                value={fieldData.dataType}
                onChange={(e) => setFieldData({ ...fieldData, dataType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                <option value="string">String</option>
                <option value="integer">Integer</option>
                <option value="decimal">Decimal</option>
                <option value="boolean">Boolean</option>
                <option value="date">Date</option>
                <option value="datetime">DateTime</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Input Type <span className="text-red-600">*</span>
              </label>
              <select
                value={fieldData.inputType}
                onChange={(e) => setFieldData({ ...fieldData, inputType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="tel">Phone</option>
                <option value="url">URL</option>
                <option value="textarea">Textarea</option>
                <option value="select">Select</option>
                <option value="checkbox">Checkbox</option>
                <option value="date">Date</option>
                <option value="number">Number</option>
              </select>
            </div>
          </div>

          {/* AI-Generated Database Mapping Preview */}
          {aiMapping && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 dark:text-white flex items-center space-x-2">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  <span>AI-Generated Database Mapping</span>
                </h4>
                <button
                  type="button"
                  onClick={generateDatabaseMapping}
                  disabled={generatingMapping}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                >
                  {generatingMapping ? 'Generating...' : 'Regenerate'}
                </button>
              </div>
              
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg p-4 space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Table:</span>
                    <span className="ml-2 text-gray-900 dark:text-white font-mono">{aiMapping.table}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Column:</span>
                    <span className="ml-2 text-gray-900 dark:text-white font-mono">{aiMapping.column}</span>
                  </div>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">SQL Type:</span>
                  <div className="mt-1 font-mono text-xs text-gray-900 dark:text-white bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-200 dark:border-gray-700">
                    {aiMapping.type}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Display Options</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={fieldData.showInList}
                  onChange={(e) => setFieldData({ ...fieldData, showInList: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-700 dark:text-gray-300">Show in List</label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={fieldData.showInForm}
                  onChange={(e) => setFieldData({ ...fieldData, showInForm: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-700 dark:text-gray-300">Show in Form</label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={fieldData.showInDetail}
                  onChange={(e) => setFieldData({ ...fieldData, showInDetail: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-700 dark:text-gray-300">Show in Detail</label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={fieldData.required}
                  onChange={(e) => setFieldData({ ...fieldData, required: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-700 dark:text-gray-300">Required Field</label>
              </div>
            </div>
          </div>

          {/* Autocomplete Suggestions - only for text fields */}
          {fieldData.inputType === 'text' && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">Autocomplete Suggestions</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Enable autocomplete suggestions from existing data in the database for this field.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={fieldData.suggestFromData}
                    onChange={(e) => setFieldData({ ...fieldData, suggestFromData: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="text-sm text-gray-700 dark:text-gray-300">Enable Autocomplete from Data</label>
                </div>

                {fieldData.suggestFromData && (
                  <div className="grid grid-cols-2 gap-4 ml-6">
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Min Characters
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={fieldData.suggestMinChars}
                        onChange={(e) => setFieldData({ ...fieldData, suggestMinChars: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="text-xs text-gray-400 mt-1">Min chars before showing suggestions</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        Max Suggestions
                      </label>
                      <input
                        type="number"
                        min="5"
                        max="50"
                        value={fieldData.suggestMaxSuggestions}
                        onChange={(e) => setFieldData({ ...fieldData, suggestMaxSuggestions: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="text-xs text-gray-400 mt-1">Max number of suggestions to show</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            
            <div className="flex items-center space-x-3">
              {!aiMapping && (
                <button
                  type="button"
                  onClick={generateDatabaseMapping}
                  disabled={generatingMapping || !fieldData.fieldName || !fieldData.displayName}
                  className="px-4 py-2 text-sm font-medium text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/20 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/40 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {generatingMapping ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                      <span>Generating Schema...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      <span>Generate Schema with AI</span>
                    </>
                  )}
                </button>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Adding Field...</span>
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    <span>{aiMapping ? 'Add Field' : 'Add Field (Will Auto-Generate)'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Field Modal (supports both custom fields and display name overrides)
const EditFieldModal = ({
  entityName,
  fieldName,
  fieldConfig,
  overrideConfig,
  tenantCode,
  onClose,
  onSuccess,
  onConfigUpdated,
  isCustom = false
}) => {
  const baseConfig =
    overrideConfig && Object.keys(overrideConfig).length > 0
      ? { ...overrideConfig }
      : { ...fieldConfig };
  
  const isGlobalField = !fieldConfig._tenantSpecific;

  const normaliseOptions = (config = {}) => {
    if (Array.isArray(config?.ui?.options)) {
      return config.ui.options.map((option) => ({
        value: typeof option === 'string' ? option : option?.value ?? '',
        label:
          typeof option === 'string'
            ? option
            : option?.label ?? option?.value ?? '',
        labelTH: typeof option === 'string' ? '' : option?.labelTH ?? ''
      }));
    }

    if (Array.isArray(config?.validation?.enum)) {
      return config.validation.enum.map((value) => ({
        value,
        label: value,
        labelTH: ''
      }));
    }

    return [];
  };

  const [displayName, setDisplayName] = useState(
    baseConfig.displayName || fieldName
  );
  const [displayNameTH, setDisplayNameTH] = useState(
    baseConfig.displayNameTH || baseConfig.displayName || fieldName
  );
  const [description, setDescription] = useState(
    baseConfig.description || ''
  );
  const [group, setGroup] = useState(
    baseConfig.group ||
      baseConfig.ui?.group ||
      'General'
  );
  const [inputType, setInputType] = useState(
    baseConfig.ui?.inputType || 'text'
  );
  const [showInList, setShowInList] = useState(
    baseConfig.ui?.showInList ?? true
  );
  const [showInForm, setShowInForm] = useState(
    baseConfig.ui?.showInForm ?? true
  );
  const [showInDetail, setShowInDetail] = useState(
    baseConfig.ui?.showInDetail ?? true
  );
  const [showInExport, setShowInExport] = useState(
    baseConfig.ui?.showInExport ?? true
  );
  const [order, setOrder] = useState(
    baseConfig.ui?.order ?? 100
  );
  const [required, setRequired] = useState(
    baseConfig.validation?.required ?? false
  );
  const [attributeType, setAttributeType] = useState(
    baseConfig.metadata?.attributeType || ''
  );
  const [options, setOptions] = useState(normaliseOptions(baseConfig));
  const [suggestFromData, setSuggestFromData] = useState(
    baseConfig.ui?.suggestFromData?.enabled ?? false
  );
  const [suggestMinChars, setSuggestMinChars] = useState(
    baseConfig.ui?.suggestFromData?.minChars ?? 0
  );
  const [suggestMaxSuggestions, setSuggestMaxSuggestions] = useState(
    baseConfig.ui?.suggestFromData?.maxSuggestions ?? 20
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const slugifyOptionValue = (inputValue = '', fallbackIndex = 0) => {
    const normalised = inputValue
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/_{2,}/g, '_')
      .replace(/^_|_$/g, '');

    if (normalised) return normalised;
    return `option_${fallbackIndex + 1}`;
  };

  const handleOptionChange = (index, key, value) => {
    setOptions((prev) =>
      prev.map((option, idx) => {
        if (idx !== index) return option;
        const nextOption = { ...option, [key]: value };

        if (
          key === 'label' &&
          (!option.value ||
            option.value === slugifyOptionValue(option.label || '', index))
        ) {
          nextOption.value = slugifyOptionValue(value, index);
        }

        return nextOption;
      })
    );
  };

  const handleAddOption = () => {
    setOptions((prev) => [
      ...prev,
      {
        value: '',
        label: '',
        labelTH: ''
      }
    ]);
  };

  const handleRemoveOption = (index) => {
    setOptions((prev) => prev.filter((_, idx) => idx !== index));
  };

  useEffect(() => {
    const nextConfig =
      overrideConfig && Object.keys(overrideConfig).length > 0
        ? overrideConfig
        : fieldConfig;

    setDisplayName(nextConfig.displayName || fieldName);
    setDisplayNameTH(
      nextConfig.displayNameTH ||
        nextConfig.displayName ||
        fieldName
    );
    setDescription(nextConfig.description || '');
    setGroup(nextConfig.group || nextConfig.ui?.group || 'General');
    setInputType(nextConfig.ui?.inputType || 'text');
    setShowInList(nextConfig.ui?.showInList ?? true);
    setShowInForm(nextConfig.ui?.showInForm ?? true);
    setShowInDetail(nextConfig.ui?.showInDetail ?? true);
    setShowInExport(nextConfig.ui?.showInExport ?? true);
    setOrder(nextConfig.ui?.order ?? 100);
    setRequired(nextConfig.validation?.required ?? false);
    setAttributeType(nextConfig.metadata?.attributeType || '');
    setOptions(normaliseOptions(nextConfig));
  }, [fieldName, fieldConfig, overrideConfig]);

  useEffect(() => {
    if (
      attributeType === 'choice' &&
      inputType !== 'select' &&
      inputType !== 'choice'
    ) {
      setInputType('select');
    }
  }, [attributeType, inputType]);

  useEffect(() => {
    if (
      (inputType === 'select' || inputType === 'choice' || inputType === 'dropdown') &&
      !attributeType
    ) {
      setAttributeType('choice');
    }
  }, [inputType, attributeType]);

  const isChoiceFieldEditor =
    inputType === 'select' ||
    inputType === 'choice' ||
    inputType === 'dropdown' ||
    attributeType === 'choice';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('token') || '';
      
      // For global fields, save display name overrides and UI settings like autocomplete
      if (isGlobalField) {
        const fieldOverride = {
          displayName: displayName || fieldName,
          displayNameTH: displayNameTH || displayName || fieldName,
          // Include UI settings like autocomplete
          ui: {
            ...(suggestFromData && {
              suggestFromData: {
                enabled: true,
                minChars: parseInt(suggestMinChars) || 0,
                maxSuggestions: parseInt(suggestMaxSuggestions) || 20
              }
            }),
            ...(!suggestFromData && {
              suggestFromData: { enabled: false }
            })
          }
        };
        
        const response = await fetch(
          `/api/tenant-entity-config/${entityName}/field-override`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
            },
            body: JSON.stringify({
              fieldName,
              override: fieldOverride
            })
          }
        );

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.message || 'Failed to update field settings');
        }

        alert('Field settings updated successfully!');
        if (onConfigUpdated) {
          onConfigUpdated(entityName);
        }
        onSuccess();
        return;
      }
      
      // For custom fields, save full configuration
      const updatedConfig = {
        ...baseConfig,
        displayName: displayName || fieldName,
        displayNameTH:
          displayNameTH || displayName || fieldName,
        description,
        group
      };

      updatedConfig.ui = {
        ...(baseConfig.ui || {}),
        inputType,
        showInList,
        showInForm,
        showInDetail,
        showInExport,
        order: Number(order) || 100,
        group,
        ...(suggestFromData && inputType === 'text' && {
          suggestFromData: {
            enabled: true,
            minChars: parseInt(suggestMinChars) || 0,
            maxSuggestions: parseInt(suggestMaxSuggestions) || 20
          }
        }),
        ...(!suggestFromData && updatedConfig.ui?.suggestFromData && {
          suggestFromData: undefined
        })
      };

      if (attributeType) {
        updatedConfig.metadata = {
          ...(baseConfig.metadata || {}),
          attributeType,
          isCustomAttribute:
            baseConfig.metadata?.isCustomAttribute ?? true,
          attributeSource:
            baseConfig.metadata?.attributeSource || 'attributes'
        };
      } else if (baseConfig.metadata) {
        updatedConfig.metadata = {
          ...(baseConfig.metadata || {}),
          attributeType: undefined,
          isCustomAttribute:
            baseConfig.metadata?.isCustomAttribute ?? true,
          attributeSource:
            baseConfig.metadata?.attributeSource || 'attributes'
        };
      }

      const baseValidation = { ...(baseConfig.validation || {}) };

      if (isChoiceFieldEditor) {
        const seenValues = new Set();
        const sanitisedOptions = options
          .map((option, index) => {
            const label = (option.label || '').trim();
            const labelTH = (option.labelTH || '').trim();
            const valueSource = (option.value || '').trim() || label;

            if (!label && !valueSource) {
              return null;
            }

            let baseValue = slugifyOptionValue(valueSource, index);
            let uniqueValue = baseValue;
            let counter = 1;

            while (seenValues.has(uniqueValue)) {
              counter += 1;
              uniqueValue = slugifyOptionValue(`${baseValue}_${counter}`, index + counter);
            }

            seenValues.add(uniqueValue);

            return {
              value: uniqueValue,
              label: label || uniqueValue,
              ...(labelTH ? { labelTH } : {})
            };
          })
          .filter(Boolean);

        if (sanitisedOptions.length === 0) {
          throw new Error('Please provide at least one option with value and label.');
        }

        updatedConfig.ui.options = sanitisedOptions;
        baseValidation.enum = sanitisedOptions.map((option) => option.value);
      } else {
        if (updatedConfig.ui?.options) {
          delete updatedConfig.ui.options;
        }
        if (baseValidation?.enum) {
          delete baseValidation.enum;
        }
      }

      baseValidation.required = required;
      updatedConfig.validation = baseValidation;

      const response = await fetch(
        `/api/tenant-entity-config/${entityName}/custom-field`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
          },
          body: JSON.stringify({
            fieldName,
            fieldConfig: updatedConfig
          })
        }
      );

      if (!response.ok) {
        const data = await response
          .json()
          .catch(() => ({}));
        throw new Error(
          data.message || 'Failed to update custom field'
        );
      }

      alert('Custom field updated successfully!');
      if (onConfigUpdated) {
        onConfigUpdated(entityName);
      }
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isGlobalField ? `Edit Display Names: ${fieldName}` : `Edit Custom Field: ${fieldName}`}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {isGlobalField && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 flex items-start space-x-2">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="font-semibold">Global Field Override</p>
                <p className="mt-1">You can customize the display names for this global field. Other properties are managed globally.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Display Name (English) <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Display Name (Thai)
              </label>
              <input
                type="text"
                value={displayNameTH}
                onChange={(e) => setDisplayNameTH(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="เช่น ข้อมูลเพิ่มเติม"
              />
            </div>
          </div>

          {!isGlobalField && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows="2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Group
                  </label>
                  <input
                    type="text"
                    value={group}
                    onChange={(e) => setGroup(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Attribute Type
                  </label>
                  <select
                    value={attributeType}
                    onChange={(e) => setAttributeType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">(Auto)</option>
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="boolean">Boolean</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Input Type
                  </label>
                  <select
                    value={inputType}
                    onChange={(e) => setInputType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="text">Text</option>
                    <option value="textarea">Textarea</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="datetime">DateTime</option>
                    <option value="select">Select</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="email">Email</option>
                    <option value="tel">Phone</option>
                    <option value="url">URL</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={order}
                    onChange={(e) => setOrder(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    min="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showInList}
                    onChange={(e) => setShowInList(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Show in List</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showInForm}
                    onChange={(e) => setShowInForm(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Show in Form</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showInDetail}
                    onChange={(e) => setShowInDetail(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Show in Detail</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showInExport}
                    onChange={(e) => setShowInExport(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Show in Export</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={required}
                    onChange={(e) => setRequired(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Required Field</span>
                </label>
              </div>
            </>
          )}

          {/* Autocomplete Suggestions - for text/string fields (both custom and global) */}
          {(inputType === 'text' || baseConfig?.dataType === 'string') && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/40 space-y-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Autocomplete Suggestions</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Enable autocomplete suggestions from existing data in the database for this field.
                Users will see a dropdown with values from existing records as they type.
              </p>
              
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={suggestFromData}
                  onChange={(e) => setSuggestFromData(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Enable Autocomplete from Data</span>
              </label>

              {suggestFromData && (
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Min Characters
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={suggestMinChars}
                      onChange={(e) => setSuggestMinChars(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-400 mt-1">Min chars before showing</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Max Suggestions
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="50"
                      value={suggestMaxSuggestions}
                      onChange={(e) => setSuggestMaxSuggestions(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-400 mt-1">Max suggestions to show</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {isChoiceFieldEditor && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/40 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Choice Options
                </h4>
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="inline-flex items-center space-x-2 px-3 py-1.5 text-sm font-medium bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Option</span>
                </button>
              </div>

              {options.length === 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No options defined yet. Click &ldquo;Add Option&rdquo; to create the first choice.
                </p>
              )}

              {options.map((option, index) => (
                <div
                  key={`${option.value || 'option'}-${index}`}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
                        Value
                      </label>
                      <input
                        type="text"
                        value={option.value}
                        onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                        placeholder="e.g. premium"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
                        Label (English)
                      </label>
                      <input
                        type="text"
                        value={option.label}
                        onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                        placeholder="e.g. Premium"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
                        Label (Thai)
                      </label>
                      <input
                        type="text"
                        value={option.labelTH}
                        onChange={(e) => handleOptionChange(index, 'labelTH', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
                        placeholder="เช่น พรีเมียม"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(index)}
                      className="inline-flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              ))}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Values should be unique and contain only letters, numbers, or underscores. Leave the value blank to generate it automatically from the label.
              </p>
            </div>
          )}

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/40">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Database Mapping (read-only)
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-700 dark:text-gray-300">
              <div>
                <span className="font-medium">Table:</span>{' '}
                <span className="font-mono">
                  {baseConfig.database?.table || 'N/A'}
                </span>
              </div>
              <div>
                <span className="font-medium">Column:</span>{' '}
                <span className="font-mono">
                  {baseConfig.database?.column || 'N/A'}
                </span>
              </div>
              <div className="col-span-2">
                <span className="font-medium">SQL Type:</span>
                <div className="mt-1 font-mono text-xs bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-200 dark:border-gray-700">
                  {baseConfig.database?.type || 'N/A'}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Validation Modal Component
const ValidationModal = ({ entityName, validation, onClose, onFieldsRemoved }) => {
  const [selectedFields, setSelectedFields] = useState([]);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);

  const handleToggleField = (fieldName) => {
    setSelectedFields(prev => 
      prev.includes(fieldName)
        ? prev.filter(f => f !== fieldName)
        : [...prev, fieldName]
    );
  };

  const handleSelectAll = () => {
    if (selectedFields.length === validation.invalidFields.length) {
      setSelectedFields([]);
    } else {
      setSelectedFields([...validation.invalidFields]);
    }
  };

  const handleRemoveFields = async () => {
    if (selectedFields.length === 0) {
      alert('Please select at least one field to remove');
      return;
    }

    const confirmMessage = `Are you sure you want to remove ${selectedFields.length} field(s) from ${entityName}?\n\n` +
      `Fields to be removed:\n${selectedFields.join(', ')}\n\n` +
      `This action cannot be undone!`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setRemoving(true);
    setError(null);

    try {
      const response = await fetch(`/api/tenant-entity-config/${entityName}/remove-fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ fields: selectedFields })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to remove fields');
      }

      const result = await response.json();
      alert(`✅ Successfully removed ${result.result.totalRemoved} field(s) from ${entityName}`);
      onFieldsRemoved();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemoving(false);
    }
  };

  const allIssues = [
    ...validation.missingColumns,
    ...validation.missingTables
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
              {validation.isValid ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <span>Validation Passed: {entityName}</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  <span>Validation Issues Found: {entityName}</span>
                </>
              )}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {validation.fieldsChecked} fields checked • {validation.invalidFields.length} invalid fields found
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          {validation.isValid ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-6 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400 mx-auto mb-3" />
              <h4 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">
                All Fields Valid!
              </h4>
              <p className="text-sm text-green-700 dark:text-green-300">
                All {validation.fieldsChecked} fields in this entity are properly mapped to existing database tables and columns.
              </p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-2">
                  Validation Summary
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-orange-700 dark:text-orange-300">Missing Columns:</span>
                    <span className="ml-2 font-semibold text-orange-900 dark:text-orange-100">
                      {validation.missingColumns.length}
                    </span>
                  </div>
                  <div>
                    <span className="text-orange-700 dark:text-orange-300">Missing Tables:</span>
                    <span className="ml-2 font-semibold text-orange-900 dark:text-orange-100">
                      {validation.missingTables.length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Select All */}
              {validation.invalidFields.length > 0 && (
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedFields.length === validation.invalidFields.length && validation.invalidFields.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      Select All Invalid Fields ({validation.invalidFields.length})
                    </span>
                  </label>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedFields.length} selected
                  </span>
                </div>
              )}

              {/* Issues List */}
              <div className="space-y-3">
                {allIssues.map((issue, index) => (
                  <div
                    key={`${issue.field}-${index}`}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(issue.field)}
                          onChange={() => handleToggleField(issue.field)}
                          className="mt-1 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {issue.field}
                            </span>
                            {issue.displayName && issue.displayName !== issue.field && (
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                ({issue.displayName})
                              </span>
                            )}
                          </div>
                          <div className="text-sm space-y-1">
                            <div className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                              <Table className="h-3 w-3" />
                              <span>Table: <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">{issue.table}</code></span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                              <Columns className="h-3 w-3" />
                              <span>Column: <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">{issue.column}</code></span>
                            </div>
                            {issue.group && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Group: {issue.group}
                              </div>
                            )}
                          </div>
                          <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                            {validation.missingTables.some(t => t.field === issue.field)
                              ? `⚠️ Table "${issue.table}" does not exist in schema`
                              : `⚠️ Column "${issue.column}" does not exist in table "${issue.table}"`
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {!validation.isValid && selectedFields.length > 0 && (
              <span className="text-orange-600 dark:text-orange-400 font-medium">
                ⚠️ {selectedFields.length} field(s) selected for removal
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Close
            </button>
            {!validation.isValid && selectedFields.length > 0 && (
              <button
                onClick={handleRemoveFields}
                disabled={removing}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 dark:bg-red-500 rounded-lg hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {removing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Removing...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Remove Selected Fields ({selectedFields.length})</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantEntityConfig;